/**
 * Quantity Display Component
 * Displays quantity with unit and formatting
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { QuantityUnit, QuantityUnitConfig } from '@/types/inventory';
import { cn } from '@/lib/utils';

interface QuantityDisplayProps {
  quantity: number;
  unit: QuantityUnit;
  variant?: 'default' | 'compact' | 'large';
  showLabel?: boolean;
  className?: string;
}

export function QuantityDisplay({
  quantity,
  unit,
  variant = 'default',
  showLabel = false,
  className
}: QuantityDisplayProps) {
  const config = QuantityUnitConfig[unit];

  // Fallback config if unit is invalid/undefined
  const safeConfig = config || { label: 'Unit', symbol: 'unit' };

  const formattedQuantity = quantity.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  });

  if (variant === 'compact') {
    return (
      <span className={cn('text-sm text-gray-600', className)}>
        {formattedQuantity} {safeConfig.symbol}
      </span>
    );
  }

  if (variant === 'large') {
    return (
      <div className={cn('flex flex-col', className)}>
        <span className="text-3xl font-bold text-gray-900">{formattedQuantity}</span>
        <span className="text-sm text-gray-500 mt-1">{safeConfig.label}</span>
      </div>
    );
  }

  return (
    <div className={cn('inline-flex items-baseline gap-1', className)}>
      {showLabel && <span className="text-sm text-gray-500 mr-1">{safeConfig.label}:</span>}
      <span className="font-semibold text-gray-900">{formattedQuantity}</span>
      <span className="text-sm text-gray-600">{safeConfig.symbol}</span>
    </div>
  );
}
